import { collection, getDocs, doc, getDoc, addDoc, setDoc, deleteDoc, arrayRemove, arrayUnion, writeBatch } from "firebase/firestore";
import { firebase, firebase_auth } from "../firebase-config";
import { COLLECTIONS } from '../config';

const collectionName = COLLECTIONS.GROUPS;

const handleError = (error, onError) => {
  console.error("Error: ", error);
  onError && onError({ status: false, error: error.message || error });
};

export const getGroupsList = async (options = {}, onSuccess, onError) => {
  try {
    const querySnapshot = await getDocs(collection(firebase, COLLECTIONS.GROUPS));
    const results = querySnapshot.docs.map(doc => ({
      groupId: doc.id,
      ...doc.data(),
    }));
    if (typeof onSuccess === 'function') {
      onSuccess(results);
    } else {
      console.error("onSuccess is not a function");
    }
  } catch (error) {
    handleError(error, onError);
  }
};

export const getOneGroup = async (id, onSuccess, onError) => {
  try {
    const docRef = doc(firebase, collectionName, id);
    const docSnapshot = await getDoc(docRef);
    if (!docSnapshot.exists()) {
      return handleError({ message: "Group not found" }, onError);
    }
    const data = { ...docSnapshot.data(), groupId: docSnapshot.id };
    onSuccess && onSuccess({ status: true, data });
  } catch (error) {
    handleError(error, onError);
  }
};

export const addGroup = async (formData, onSuccess, onError) => {
  try {
    const currentUserId = firebase_auth.currentUser.uid;
    const docRef = await addDoc(collection(firebase, collectionName), {
      ...formData,
      employeeId: formData?.employeeId,
      createdBy: currentUserId,
      createdAt: new Date().getTime(),
    });

    if (formData?.employeeId?.length > 0) {
      const batch = writeBatch(firebase);
      formData?.employeeId?.forEach((empId) => {
        const empRef = doc(firebase, COLLECTIONS.USERS, empId);
        batch.update(empRef, {
          group: arrayUnion(docRef.id),
        });
      });
      await batch.commit();
    }

    const responseGroupItem = { ...formData, groupId: docRef.id };
    onSuccess && onSuccess({ status: true, data: responseGroupItem });
  } catch (error) {
    handleError(error, onError);
  }
};

export const updateGroup = async (id, formData, onSuccess, onError) => {
  try {
    const currentUserId = firebase_auth.currentUser.uid;
    const docRef = doc(firebase, collectionName, id);
    const docContent = {
      title: formData?.title,
      description: formData?.description,
      type: formData?.type,
      employeeId: formData?.employeeId,
      updatedAt: new Date().getTime(),
      updatedBy: currentUserId,
    };
    await setDoc(docRef, docContent, { merge: true });

    const newEmployeeIds = formData?.employeeId || [];
    const oldEmployeeIds = formData?.oldEmployeeId || [];

    const toAdd = newEmployeeIds?.filter((id) => !oldEmployeeIds?.includes(id));
    const toRemove = oldEmployeeIds?.filter((id) => !newEmployeeIds?.includes(id));

    const batch = writeBatch(firebase);

    toAdd?.forEach((empId) => {
      const empRef = doc(firebase, COLLECTIONS.USERS, empId);
      batch.update(empRef, {
        group: arrayUnion(id),
      });
    });

    toRemove?.forEach((empId) => {
      const empRef = doc(firebase, COLLECTIONS.USERS, empId);
      batch.update(empRef, {
        group: arrayRemove(id),
      });
    });

    await batch.commit();

    onSuccess && onSuccess({ status: true, message: "Updated successfully" });
  } catch (error) {
    handleError(error, onError);
  }
};

export const deleteGroup = async (id, onSuccess, onError) => {
  try {
    const docRef = doc(firebase, collectionName, id);
    const docSnapshot = await getDoc(docRef);

    if (!docSnapshot.exists()) {
      return handleError({ message: "Group not found" }, onError);
    }

    const data = docSnapshot.data();
    if (data?.employeeId?.length > 0) {
      const promises = data.employeeId.map(empid =>
        setDoc(doc(firebase, COLLECTIONS.EMPLOYEE, empid), {
          group: arrayRemove(id)
        }, { merge: true })
      );
      await Promise.all(promises);
    }

    await deleteDoc(docRef);
    onSuccess && onSuccess({ status: true, message: "Delete successful" });
  } catch (error) {
    handleError(error, onError);
  }
};
